import { gsap } from "gsap";
import { LOOPING_TEXT_EFFECTS, TEXT_EFFECTS } from "@excalidraw/element";
import { useEffect, useRef, useState } from "react";

import type { AnimatedValues } from "@excalidraw/element";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import { createAnimationTarget } from "../animation/runtime";

import { observeVisibility } from "./visibility";

import type { AnimationPreset } from "../animation/presets";

/** How far preview shapes travel per scene unit — presets use canvas-scale
 * distances (100–300px), the preview box is ~40px. */
const TRANSLATE_SCALE = 0.12;
const MAX_TRANSLATE = 14;

const clampTranslate = (value: number) =>
  Math.max(-MAX_TRANSLATE, Math.min(MAX_TRANSLATE, value * TRANSLATE_SCALE));

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

/** Stand-in element the preset builds against. Typed as a text element so
 * text presets (typewriter char-stepping) behave like they would on canvas. */
const PREVIEW_ELEMENT = {
  id: "__preset-preview__",
  type: "text",
  x: 0,
  y: 0,
  width: 40,
  height: 40,
  text: "Preview",
  strokeGradient: {
    type: "linear" as const,
    angle: 0,
    stops: [
      { offset: 0, color: "#ff0000" },
      { offset: 1, color: "#0000ff" },
    ],
  },
} as unknown as ExcalidrawElement;

/** Deterministic per-index hash → [0, 1) — same dot layout every frame. */
const prand = (i: number) => {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

const DOT_COLOR = "rgba(128, 128, 128, 0.95)";
const dot = (x: number, y: number, r: number) =>
  `radial-gradient(circle ${r.toFixed(2)}px at ${x.toFixed(1)}% ${y.toFixed(
    1,
  )}%, ${DOT_COLOR} 0 ${r.toFixed(2)}px, transparent ${r.toFixed(2)}px)`;

/** Exported for the preview-fidelity test: every gallery preset must produce
 * visible style changes through this mapping, or its tile looks dead. */
export const applyToStyle = (el: HTMLElement, t: AnimatedValues) => {
  // per-char text effects animate glyphs on canvas; on the tiny "Ab" tile we
  // show a representative motion: a bob for looping effects, a shuffle for
  // scramble, a left-to-right reveal for staggered entrances
  let textDx = 0;
  let textDy = 0;
  let textClip: string | null = null;
  if (t.textProgress !== undefined) {
    const p = clamp01(t.textProgress);
    if (LOOPING_TEXT_EFFECTS.has(t.textEffect ?? -1)) {
      textDy = Math.sin(2 * Math.PI * p * 2) * 3;
    } else if (t.textEffect === TEXT_EFFECTS.scramble) {
      textDx = p < 1 ? Math.sin(p * 40) * 1.5 : 0;
    } else {
      textClip = `inset(0 ${(1 - p) * 100}% 0 0)`;
    }
  }
  // text on path: glide the glyphs along a small arc
  if (t.textPathProgress !== undefined) {
    const p = clamp01(t.textPathProgress);
    textDx += (p - 0.5) * 24;
    textDy += -Math.sin(Math.PI * p) * 7;
  }
  // counter/odometer: show the actual interpolated number
  if (t.counterProgress !== undefined) {
    const p = clamp01(t.counterProgress);
    const from = t.counterFrom ?? 0;
    const to = t.counterTo ?? 100;
    el.textContent = (from + (to - from) * p).toFixed(
      Math.min(4, Math.max(0, Math.round(t.counterDecimals ?? 0))),
    );
  }

  // dash flow: marching monochrome stripes across the tile shape
  if (t.dashOffset !== undefined) {
    el.style.backgroundImage =
      "repeating-linear-gradient(90deg, transparent 0 4px, rgba(128, 128, 128, 0.85) 4px 8px)";
    el.style.backgroundPositionX = `${-t.dashOffset}px`;
  }
  // flow dots: a small dot sweeping across the tile
  if (t.flowPhase !== undefined) {
    const p = ((t.flowPhase % 1) + 1) % 1;
    el.style.backgroundImage =
      "radial-gradient(circle 3px at var(--flow-x, 0%) 50%, rgba(128, 128, 128, 0.95) 0 3px, transparent 3px)";
    el.style.setProperty("--flow-x", `${p * 100}%`);
  }
  // particle emitters (confetti/sparkle) and fragment effects (explode /
  // shatter / assemble): a handful of gray dots inside the tile stand in for
  // the canvas particles/shards
  if (t.emitProgress !== undefined || t.fragProgress !== undefined) {
    const dots: string[] = [];
    if (t.emitProgress !== undefined) {
      const p = clamp01(t.emitProgress);
      const n = Math.min(8, Math.max(4, Math.round((t.emitCount ?? 24) / 5)));
      for (let i = 0; i < n; i++) {
        if ((t.emitKind ?? 0) === 1) {
          // sparkle: dots twinkling in place over the shape
          const a = prand(i) * 2 * Math.PI;
          const r = 10 + prand(i + 5) * 25;
          const phase = (p * 2 + prand(i + 11)) % 1;
          const size = 1.8 * Math.sin(Math.PI * phase) * (t.emitSize ?? 1);
          if (size > 0.2) {
            dots.push(dot(50 + Math.cos(a) * r, 50 + Math.sin(a) * r, size));
          }
        } else if (p > 0.01) {
          // confetti: burst upward, then rain down under gravity
          const spread = t.emitSpread ?? 1;
          const a = -Math.PI / 2 + (prand(i) - 0.5) * Math.PI * spread;
          const v = 26 + prand(i + 9) * 18;
          const x = 50 + Math.cos(a) * v * p * spread;
          const y =
            50 + Math.sin(a) * v * p + (t.emitGravity ?? 1) * 55 * p * p;
          const size = 1.7 * (1 - 0.6 * p);
          if (x > -5 && x < 105 && y > -5 && y < 105 && size > 0.2) {
            dots.push(dot(x, y, size));
          }
        }
      }
    }
    if (t.fragProgress !== undefined && t.fragProgress > 0.01) {
      const p = clamp01(t.fragProgress);
      const n = Math.min(8, Math.max(3, Math.round(t.fragCount ?? 6)));
      for (let i = 0; i < n; i++) {
        const a = (i / n) * 2 * Math.PI + prand(i) * 0.8;
        const r = p * (18 + prand(i + 3) * 20) * (t.fragSpread ?? 1);
        const x = 50 + Math.cos(a) * r;
        const y = 50 + Math.sin(a) * r + (t.fragGravity ?? 1) * 22 * p * p;
        dots.push(dot(x, y, 2.2 * (1 - 0.4 * p)));
      }
    }
    el.style.backgroundImage = dots.join(", ");
  }
  // shape morph: square → circle via border radius
  if (t.morphProgress !== undefined) {
    el.style.borderRadius = `${3 + clamp01(t.morphProgress) * 47}%`;
  }
  // color tweens: show the actual animated color — the white tile background
  // gives it full contrast, so no desaturation stand-in
  const animatedColor = t.strokeColor ?? t.backgroundColor;
  if (animatedColor !== undefined) {
    el.style.backgroundColor = animatedColor;
  }
  // stroke-gradient presets (rotate/march/recolor): render the actual
  // animated gradient so the tile shows real motion, not a stand-in
  if (t.gradientAngleDelta || t.gradientOffsetShift || t.gradientStopColors) {
    const stops = t.gradientStopColors ?? ["#ff0000", "#0000ff"];
    const angle = t.gradientAngleDelta ?? 0;
    const shift = (((t.gradientOffsetShift ?? 0) % 1) + 1) % 1;
    const n = stops.length;
    const stopsCss = stops
      .map((color, i) => {
        const offset = ((i / Math.max(1, n - 1) + shift) % 1) * 100;
        return `${color} ${offset.toFixed(1)}%`;
      })
      .join(", ");
    el.style.backgroundImage = `linear-gradient(${angle}deg, ${stopsCss})`;
  }
  // line draw-on: left-to-right reveal; with drawStart, a traveling window
  // (drawSegment) instead of a growing one
  if (t.drawProgress !== undefined && textClip === null) {
    const end = clamp01(t.drawProgress);
    const start = clamp01(t.drawStart ?? 0);
    textClip = `inset(0 ${(1 - end) * 100}% 0 ${start * 100}%)`;
  }

  // boil / sketch-in: the renderer re-rolls the rough seed per exposure —
  // approximate with a small deterministic jitter stepped per frame
  let jx = 0;
  let jy = 0;
  let jr = 0;
  if ((t.seedShift ?? 0) >= 1) {
    const frame = Math.floor(t.seedShift ?? 0);
    const amp = Math.min(2, 0.7 + (t.roughnessDelta ?? 0) * 0.6);
    jx = (prand(frame * 3 + 1) - 0.5) * 2 * amp;
    jy = (prand(frame * 3 + 2) - 0.5) * 2 * amp;
    jr = (prand(frame * 3 + 3) - 0.5) * 0.07;
  }

  // rotate/scale/skew pivot around the animated anchor (swing hangs from an
  // edge, not its center)
  el.style.transformOrigin = `${(t.anchorX ?? 0.5) * 100}% ${
    (t.anchorY ?? 0.5) * 100
  }%`;

  const transform = [
    `translate(${clampTranslate(t.dx ?? 0) + textDx + jx}px, ${
      clampTranslate(t.dy ?? 0) + textDy + jy
    }px)`,
    `rotate(${(t.angleDelta ?? 0) + jr}rad)`,
    `scale(${t.scaleX ?? 1}, ${t.scaleY ?? 1})`,
    `skew(${t.skewX ?? 0}rad, ${t.skewY ?? 0}rad)`,
  ];
  el.style.transform = transform.join(" ");
  // fragment effects scatter the shape into shards — fade the intact body as
  // the shards (dots) fly
  const fragFade =
    t.fragProgress !== undefined ? 1 - 0.85 * clamp01(t.fragProgress) : 1;
  el.style.opacity = String(clamp01((t.opacity ?? 1) * fragFade));

  const filters: string[] = [];
  if (t.blur) {
    filters.push(`blur(${Math.min(t.blur, 12) * 0.35}px)`);
  }
  if (t.brightness !== undefined && t.brightness !== 1) {
    filters.push(`brightness(${Math.max(0, t.brightness)})`);
  }
  if (t.invert) {
    filters.push(`invert(${clamp01(t.invert)})`);
  }
  // the shape carries its category accent color, so real hue rotation reads
  // correctly — same filter the canvas renderer applies
  if (t.hueRotate) {
    filters.push(`hue-rotate(${t.hueRotate}deg)`);
  }
  if (t.saturate !== undefined && t.saturate !== 1) {
    filters.push(`saturate(${Math.max(0, t.saturate)})`);
  }
  if (t.grayscale) {
    filters.push(`grayscale(${clamp01(t.grayscale)})`);
  }
  if (t.contrast !== undefined && t.contrast !== 1) {
    filters.push(`contrast(${Math.max(0, t.contrast)})`);
  }
  if (t.sepia) {
    filters.push(`sepia(${clamp01(t.sepia)})`);
  }
  el.style.filter = filters.join(" ");

  if (t.revealProgress !== undefined) {
    const p = clamp01(t.revealProgress);
    const hidden = (1 - p) * 100;
    switch (t.revealDir ?? 0) {
      case 1: // grows from the right
        el.style.clipPath = `inset(0 0 0 ${hidden}%)`;
        break;
      case 2: // grows from the top
        el.style.clipPath = `inset(0 0 ${hidden}% 0)`;
        break;
      case 3: // grows from the bottom
        el.style.clipPath = `inset(${hidden}% 0 0 0)`;
        break;
      default: // grows from the left
        el.style.clipPath = `inset(0 ${hidden}% 0 0)`;
    }
  } else {
    el.style.clipPath = textClip ?? "";
  }
};

/** A looping monochrome thumbnail of a preset, driven by the preset's real
 * GSAP build so the tile shows exactly what the animation does. */
export const PresetPreview = ({ preset }: { preset: AnimationPreset }) => {
  const shapeRef = useRef<HTMLSpanElement>(null);
  // The gallery mounts ~140 of these at once. Building a preset's GSAP
  // timeline is the expensive part, so it waits until the tile is actually
  // on screen — mounting is then just spans + one shared observer, and only
  // the handful of visible tiles pay for a timeline.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const shape = shapeRef.current;
    return shape ? observeVisibility(shape, setVisible) : undefined;
  }, []);

  useEffect(() => {
    const shape = shapeRef.current;
    if (!shape || !visible) {
      return;
    }
    // the runtime's fully-seeded target — a hand-rolled partial one makes
    // GSAP silently no-op on any missing key ("Missing plugin?" warning)
    const target: AnimatedValues = createAnimationTarget();
    const tl = gsap.timeline({
      // built paused, played below — this effect only runs for tiles the
      // visibility observer says are on screen
      paused: true,
      repeat: -1,
      repeatDelay: 0.6,
      onUpdate: () => applyToStyle(shape, target),
      onRepeat: () => applyToStyle(shape, target),
    });
    const cfg = {
      preset: preset.id,
      ...preset.defaults,
      // finite loops only — the outer timeline repeat handles looping
      repeat: Math.max(0, Math.min(preset.defaults.repeat, 1)),
      delay: 0,
      params: { ...preset.defaults.params },
    };
    preset.build(tl, target, PREVIEW_ELEMENT, cfg);
    // stroke-style presets (dash flow, flow dots, draw-on) animate along a
    // line on canvas — render their tile as an arrow instead of a square so
    // the dashes/dots visibly travel along a stroke
    shape.classList.toggle(
      "preset-preview__shape--stroke",
      target.dashOffset !== undefined ||
        target.flowPhase !== undefined ||
        target.drawProgress !== undefined,
    );
    // gradientRotate/March tween channels that are seeded 0, so applyToStyle's
    // truthy guard (deliberate — every preset seeds them) misses frame 0 and
    // the tile starts without its gradient. Seed the stop array up front, the
    // same trick gradientColors uses; the colors match applyToStyle's own
    // fallback, so only the first frame changes.
    if (
      target.gradientStopColors === undefined &&
      tl
        .getChildren(true, true, false)
        .some(
          (child) =>
            "gradientAngleDelta" in child.vars ||
            "gradientOffsetShift" in child.vars,
        )
    ) {
      target.gradientStopColors = ["#ff0000", "#0000ff"];
    }
    // exit/one-way presets end hidden (opacity/scale 0, wiped away) — glide
    // back to the rest state before the loop repeats so the tile never sits
    // blank during the repeat delay
    const rest: Record<string, number> = {
      dx: 0,
      dy: 0,
      angleDelta: 0,
      scaleX: 1,
      scaleY: 1,
      skewX: 0,
      skewY: 0,
      opacity: 1,
      hueRotate: 0,
      brightness: 1,
      saturate: 1,
      grayscale: 0,
      invert: 0,
      blur: 0,
      contrast: 1,
      sepia: 0,
    };
    if (target.revealProgress !== undefined) {
      rest.revealProgress = 1;
    }
    if (target.drawProgress !== undefined) {
      rest.drawProgress = 1;
    }
    if (target.drawStart !== undefined) {
      rest.drawStart = 0;
    }
    if (target.fragProgress !== undefined) {
      rest.fragProgress = 0;
    }
    if (target.emitProgress !== undefined) {
      rest.emitProgress = 0;
    }
    if (target.textProgress !== undefined) {
      rest.textProgress = 1;
    }
    if (target.morphProgress !== undefined) {
      rest.morphProgress = 0;
    }
    tl.to(target, { ...rest, duration: 0.3, ease: "power1.inOut" }, ">0.25");
    applyToStyle(shape, target);
    // reduced motion: tiles stay static (the tile still shows the rest state)
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduceMotion) {
      tl.play();
    }
    return () => {
      tl.kill();
    };
  }, [preset, visible]);

  const isText = preset.category === "text";
  return (
    <span className="preset-preview" aria-hidden="true">
      <span
        className={`preset-preview__shape${
          isText ? " preset-preview__shape--text" : ""
        }`}
        ref={shapeRef}
      >
        {isText ? "Abc" : null}
      </span>
    </span>
  );
};
